import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useWorkspaces } from '@/hooks/ReactQueryHooks/useWorkspaceQuery';
import { workspaceSelector } from '@/store';
import { setCurrentWorkspace } from '@/store/Workspace';
import type { IWorkspace } from '@/types/Workspace';
import { getCurrentLocalStorageWorkspace } from '@/utils/localstorage';

export function isRealWorkspace(workspace?: IWorkspace | null): workspace is IWorkspace & { id: number } {
  return !!workspace?.id && workspace.id > 0 && workspace.archived !== true && workspace.isActive !== false;
}

export function selectDefaultWorkspace(workspaces: IWorkspace[], currentWorkspace?: IWorkspace | null): IWorkspace | undefined {
  if (isRealWorkspace(currentWorkspace)) {
    return currentWorkspace;
  }

  const savedWorkspaceId = getCurrentLocalStorageWorkspace();
  const savedWorkspace = workspaces.find((workspace) => isRealWorkspace(workspace) && workspace.id === savedWorkspaceId);

  return savedWorkspace || workspaces.find(isRealWorkspace);
}

export function useEnsureCurrentWorkspace() {
  const dispatch = useDispatch();
  const { currentWorkspace } = useSelector(workspaceSelector);
  const workspacesQuery = useWorkspaces({ params: { archived: 'false' } });
  const workspaces = workspacesQuery.data || [];

  useEffect(() => {
    const defaultWorkspace = selectDefaultWorkspace(workspaces, currentWorkspace);

    if (defaultWorkspace && defaultWorkspace.id !== currentWorkspace?.id) {
      dispatch(setCurrentWorkspace(defaultWorkspace));
    }
  }, [currentWorkspace, dispatch, workspaces]);

  return {
    ...workspacesQuery,
    currentWorkspace,
    workspaces,
    hasRealWorkspace: workspaces.some(isRealWorkspace),
  };
}
